import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, RotateCcw, Settings } from 'lucide-react';
import { MIN_COLUMN_WIDTH, type ColumnSettings, type ReportColumn } from './reportColumns';

/**
 * A report table whose columns the user can show/hide (gear menu), resize (drag a heading's
 * right edge) and sort (click a heading). The settings live in useColumnSettings.
 */

export function ColumnPicker<Row>({ table, title = 'Show columns' }: { table: ColumnSettings<Row>; title?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative pdf-hide">
      <button
        type="button"
        aria-label="Choose columns"
        aria-expanded={open}
        title="Choose columns"
        onClick={() => setOpen(!open)}
        className={`p-2 rounded-lg border transition-colors ${
          open
            ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <Settings className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-2">
          <p className="px-2 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {title}
          </p>
          <div className="max-h-[min(26rem,60vh)] overflow-y-auto">
            {table.columns.map((column) => {
              const on = table.isVisible(column);
              const locked = on && table.visible.length <= 1;
              return (
                <label
                  key={column.id}
                  className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm text-gray-800 dark:text-gray-200 ${
                    locked ? 'opacity-60' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={locked}
                    onChange={() => table.toggleColumn(column.id)}
                    className="w-4 h-4 rounded accent-emerald-600"
                  />
                  {column.label}
                </label>
              );
            })}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <p className="px-2 pb-2 text-xs text-gray-500 dark:text-gray-400">
              Drag a heading&apos;s edge to resize. Click a heading to sort. The PDF and CSV follow these settings.
            </p>
            <button
              type="button"
              onClick={table.reset}
              disabled={!table.customised}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset columns
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ReportTableProps<Row> {
  table: ColumnSettings<Row>;
  /** Already sorted. */
  rows: Row[];
  rowKey: (row: Row, index: number) => string | number;
  onRowClick?: (row: Row) => void;
  /** Totals row, by column id; the first column shows "Total" unless given. */
  footer?: Record<string, ReactNode>;
  /** A fixed column at the end (e.g. download buttons) that isn't configurable. */
  trailing?: { header: string; width: number; cell: (row: Row) => ReactNode };
  /**
   * Columns that may stretch to fill spare width, in order of preference. The first one shown
   * stretches until the user resizes it; then an empty column at the end takes the slack.
   */
  stretch?: string[];
}

export function ReportTable<Row>({ table, rows, rowKey, onRowClick, footer, trailing, stretch = [] }: ReportTableProps<Row>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const colRefs = useRef(new Map<string, HTMLTableColElement>());
  const fillerRef = useRef<HTMLTableColElement>(null);
  const columns = table.visible;
  const stretchColumn = columns.find((c) => c.id === stretch.find((id) => columns.some((v) => v.id === id)));
  const stretchId = stretchColumn && !table.isResized(stretchColumn) ? stretchColumn.id : undefined;
  // Its width is the minimum; the table is never narrower than all the widths together
  const totalWidth = columns.reduce((sum, column) => sum + table.widthOf(column), 0) + (trailing?.width ?? 0);

  // Drag: move the <col> directly for smooth resizing, then save the width once on release
  const startResize = (event: PointerEvent<HTMLSpanElement>, column: ReportColumn<Row>) => {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const col = colRefs.current.get(column.id);
    const startX = event.clientX;
    // A stretched column starts from the width it's showing; the empty end column takes over the slack
    const startWidth =
      column.id === stretchId
        ? Math.round(handle.parentElement?.getBoundingClientRect().width ?? table.widthOf(column))
        : table.widthOf(column);
    const othersWidth = totalWidth - table.widthOf(column);
    let width = startWidth;
    handle.setPointerCapture(event.pointerId);
    document.body.style.cursor = 'col-resize';
    if (column.id === stretchId && fillerRef.current) fillerRef.current.style.width = '';

    const onMove = (move: globalThis.PointerEvent) => {
      width = Math.max(MIN_COLUMN_WIDTH, startWidth + move.clientX - startX);
      if (col) col.style.width = `${width}px`;
      if (tableRef.current) tableRef.current.style.minWidth = `${othersWidth + width}px`;
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      if (width !== startWidth || column.id === stretchId) table.setWidth(column.id, width);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };

  const resizeWithKeys = (event: KeyboardEvent<HTMLSpanElement>, column: ReportColumn<Row>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    table.setWidth(column.id, table.widthOf(column) + (event.key === 'ArrowRight' ? 16 : -16));
  };

  const alignClass = (column: ReportColumn<Row>) => (column.align === 'right' ? 'text-right' : 'text-left');

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table ref={tableRef} className="w-full table-fixed border-collapse" style={{ minWidth: totalWidth }}>
        <colgroup>
          {columns.map((column) => (
            <col
              key={column.id}
              ref={(el) => {
                if (el) colRefs.current.set(column.id, el);
                else colRefs.current.delete(column.id);
              }}
              style={{ width: column.id === stretchId ? undefined : table.widthOf(column) }}
            />
          ))}
          {trailing && <col style={{ width: trailing.width }} />}
          {/* Takes up spare width when no column stretches, so resizing one doesn't widen the others */}
          <col ref={fillerRef} style={{ width: stretchId ? 0 : undefined }} />
        </colgroup>
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
            {columns.map((column) => {
              const sorted = table.sort.id === column.id;
              const SortIcon = !sorted ? ArrowUpDown : table.sort.dir === 'asc' ? ArrowUp : ArrowDown;
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={sorted ? (table.sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className="group relative p-0 text-xs font-semibold text-gray-500 dark:text-gray-400"
                >
                  {column.sortValue ? (
                    <button
                      type="button"
                      onClick={() => table.toggleSort(column.id)}
                      title={`Sort by ${column.label}`}
                      className={`flex w-full items-center gap-1 px-3 py-3 hover:text-gray-900 dark:hover:text-white ${
                        column.align === 'right' ? 'flex-row-reverse' : ''
                      } ${sorted ? 'text-emerald-700 dark:text-emerald-400' : ''}`}
                    >
                      <span className="truncate">{column.label}</span>
                      <SortIcon
                        className={`w-3.5 h-3.5 shrink-0 ${sorted ? '' : 'opacity-0 group-hover:opacity-60'}`}
                      />
                    </button>
                  ) : (
                    <div className={`px-3 py-3 truncate ${alignClass(column)}`}>{column.label}</div>
                  )}
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${column.label}`}
                    tabIndex={0}
                    title="Drag to resize, double-click to reset"
                    onPointerDown={(event) => startResize(event, column)}
                    onDoubleClick={() => table.resetWidth(column.id)}
                    onKeyDown={(event) => resizeWithKeys(event, column)}
                    className="absolute right-0 top-0 z-10 h-full w-2.5 translate-x-1/2 cursor-col-resize touch-none select-none focus:outline-none after:absolute after:inset-y-2 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-gray-300 dark:after:bg-gray-600 hover:after:w-0.5 hover:after:bg-emerald-500 focus-visible:after:w-0.5 focus-visible:after:bg-emerald-500"
                  />
                </th>
              );
            })}
            {trailing && (
              <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">
                {trailing.header}
              </th>
            )}
            <th aria-hidden="true" className="p-0" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((row, index) => (
            <tr
              key={rowKey(row, index)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`text-sm text-gray-900 dark:text-gray-100 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 ${
                onRowClick ? 'cursor-pointer' : ''
              }`}
            >
              {columns.map((column) => (
                <td key={column.id} className={`px-3 py-2.5 overflow-hidden ${alignClass(column)}`}>
                  {column.cell ? column.cell(row) : <span className="block truncate">{column.text(row)}</span>}
                </td>
              ))}
              {trailing && (
                <td className="px-3 py-1.5 text-right whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
                  {trailing.cell(row)}
                </td>
              )}
              <td aria-hidden="true" className="p-0" />
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="bg-emerald-50/70 dark:bg-emerald-900/20 border-t-2 border-emerald-200 dark:border-emerald-800 text-sm font-semibold text-gray-900 dark:text-white">
              {columns.map((column, index) => (
                <td key={column.id} className={`px-3 py-3 whitespace-nowrap overflow-hidden tabular-nums ${alignClass(column)}`}>
                  {footer[column.id] ?? (index === 0 ? 'Total' : '')}
                </td>
              ))}
              {trailing && <td />}
              <td aria-hidden="true" className="p-0" />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
